// ============================================================================
// Action Filter — auth-aware filtering of available actions based on role.
// Pure functions (no React hooks) so they can be called from any context.
// ============================================================================

// ---------------------------------------------------------------------------
// Role hierarchy
// ---------------------------------------------------------------------------

/**
 * Numeric level for a role. Higher = more permissions.
 *
 * - admin  → 2 (can do everything)
 * - member → 1 (can create / edit, but not manage)
 * - other  → 0 (read-only)
 */
export function getRoleLevel(role: string): number {
  switch (role) {
    case "admin":
      return 2;
    case "member":
      return 1;
    default:
      return 0;
  }
}

// ---------------------------------------------------------------------------
// Permission map
// ---------------------------------------------------------------------------

/**
 * Minimum role required to see a given action.
 *
 * - `minRole: 'admin'`  → only admins (level 2+)
 * - `minRole: 'member'` → members and above (level 1+)
 * - No entry (or `undefined`) → everyone can see the action
 */
const ACTION_PERMISSIONS: Record<string, { minRole?: "admin" | "member" }> = {
  // Admin-only actions
  "execute-epic": { minRole: "admin" },
  "manage-members": { minRole: "admin" },
  "manage-settings": { minRole: "admin" },
  "manage-users": { minRole: "admin" },

  // Member+ actions
  "create-epic": { minRole: "member" },
  "create-request": { minRole: "member" },
  "edit-epic": { minRole: "member" },
  "add-feature": { minRole: "member" },
  "edit-feature": { minRole: "member" },
  "add-task": { minRole: "member" },
  "edit-task": { minRole: "member" },
  "change-status": { minRole: "member" },

  // Everyone — no entry needed, but listed for clarity:
  // "view-metrics", "view-activity", "filter", "search",
  // "view-team", "view-description", "view-request", "react", "comment"
};

// ---------------------------------------------------------------------------
// Filter function
// ---------------------------------------------------------------------------

/**
 * Filter a list of available actions based on the user's role.
 *
 * - Admins see all actions.
 * - Members see member+ and unrestricted actions.
 * - Other / read-only users see only unrestricted actions (no minRole).
 * - Unknown actions (not in ACTION_PERMISSIONS) default to allowed.
 */
export function filterActions(actions: string[], userRole: string): string[] {
  const level = getRoleLevel(userRole);

  // Admins see everything — short-circuit
  if (level >= 2) {
    return actions;
  }

  return actions.filter((action) => {
    const perm = ACTION_PERMISSIONS[action];

    // No entry → allowed for everyone
    if (!perm?.minRole) {
      return true;
    }

    return level >= getRoleLevel(perm.minRole);
  });
}
